<template>
  <div>
    <RctNavbar :links="navLinks" logo-href="https://ridgecodetech.com">
      <template #actions>
        <RouterLink to="/sign-in" class="rct-nav__signin">Sign In</RouterLink>
      </template>
    </RctNavbar>

    <!-- HERO -->
    <section class="hero">
      <div class="hero-content">
        <p class="hero-eyebrow">AI Engineering · Athens, AL</p>
        <h1 class="hero-headline">AI that works the way<br><em>your business does.</em></h1>
        <p class="hero-sub">From integrating frontier language models to training custom ones — AI systems built for production, not just prototypes.</p>
        <div class="hero-actions">
          <RctButton href="#contact">Start a Project</RctButton>
          <RctButton variant="ghost" href="#services">View Services →</RctButton>
        </div>
      </div>
      <div class="ridge-divider">
        <svg viewBox="0 0 1130 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="80" width="1130" height="40" fill="#f5f8ff"/>
          <path d="M0,120 L0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92 L1130,120 Z" fill="#f5f8ff"/>
          <path d="M0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92" fill="none" stroke="#becdea" stroke-width="1.5" opacity="0.6"/>
        </svg>
      </div>
    </section>

    <!-- SERVICES -->
    <section class="services" id="services">
      <div class="section-inner">
        <p class="section-label reveal">What I Build</p>
        <h2 class="section-heading reveal reveal-delay-1">Production AI for real workflows.</h2>
        <div class="services-grid">
          <div class="service-card reveal reveal-delay-1">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="8" width="28" height="20" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M10 18h4M18 14l4 4-4 4" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">LLM Integration</h3>
            <p class="service-desc">Connect state-of-the-art language models to your existing systems. APIs, streaming, context management, and safety guardrails — production-ready from day one.</p>
          </div>
          <div class="service-card reveal reveal-delay-2">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="5" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M18 4v4M18 28v4M4 18h4M28 18h4M8.5 8.5l2.8 2.8M24.7 24.7l2.8 2.8M8.5 27.5l2.8-2.8M24.7 11.3l2.8-2.8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">Retrieval-Augmented Generation</h3>
            <p class="service-desc">Ground AI responses in your own data. Knowledge bases, document search, and domain expertise — without hallucination risk on information that matters.</p>
          </div>
          <div class="service-card reveal reveal-delay-3">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 12h8M6 18h12M6 24h6" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <rect x="20" y="10" width="10" height="16" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M23 14h4M23 18h4M23 22h2" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">AI Workflow Automation</h3>
            <p class="service-desc">Replace repetitive knowledge work with AI pipelines. Document processing, classification, summarization, and extraction — integrated with your existing tools.</p>
          </div>
          <div class="service-card reveal reveal-delay-4">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6 L30 12 L30 24 L18 30 L6 24 L6 12 Z" stroke="#7a96c1" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M18 6v24M6 12l12 6 12-6" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Custom Model Training</h3>
            <p class="service-desc">Domain-specific models trained on your data and workflows. Fine-tuned for your terminology, your use cases, and your performance requirements — not generic outputs.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CUSTOM MODEL -->
    <section class="model-section" id="model">
      <div class="section-inner">
        <div class="reveal">
          <span class="model-badge">Phase 1 Live</span>
        </div>
        <p class="section-label reveal reveal-delay-1">RCT Model</p>
        <h2 class="section-heading reveal reveal-delay-2">A purpose-built AI,<br>trained from the ground up.</h2>
        <p class="section-body reveal reveal-delay-3">Most AI integrations wrap an existing model with a generic prompt. The RCT approach goes deeper. Phase 1 — a knowledge-grounded retrieval layer that grounds every response in RCT-specific content — is live and powering the AI assistant on this page. Phase 2 brings custom-weight fine-tuning for even deeper domain alignment.</p>
        <div class="model-grid reveal reveal-delay-3">
          <div class="model-card">
            <div class="model-card-number">01</div>
            <h3 class="model-card-title">Knowledge Grounding</h3>
            <p class="model-card-desc">Live in Phase 1: every response draws from a curated, domain-specific knowledge base via retrieval-augmented generation — not generic training data. The assistant already knows RCT's services, processes, and context.</p>
          </div>
          <div class="model-card">
            <div class="model-card-number">02</div>
            <h3 class="model-card-title">Use-Case Alignment</h3>
            <p class="model-card-desc">Optimized for specific task types — not a general assistant trying to do everything. Consistent, reliable output for the exact workflows it's designed for.</p>
          </div>
          <div class="model-card">
            <div class="model-card-number">03</div>
            <h3 class="model-card-title">Client-Deployable</h3>
            <p class="model-card-desc">The architecture is being packaged for client deployment — custom knowledge bases, scoped retrieval, and task-specific tuning built around your workflows and terminology.</p>
          </div>
        </div>
        <p class="model-note reveal">Interested in a knowledge-grounded AI for your business? <a href="#contact">Get in touch</a> to discuss requirements.</p>
      </div>
    </section>

    <!-- ABOUT -->
    <section class="about" id="about">
      <div class="section-inner">
        <p class="section-label reveal">Approach</p>
        <h2 class="section-heading reveal reveal-delay-1">Engineering-first AI.</h2>
        <div class="about-layout">
          <div class="about-text reveal reveal-delay-2">
            <p>AI projects fail when they're treated as product demos rather than engineering problems. I build AI systems the same way I build any other production software — with clear requirements, observable behavior, and a path to maintenance.</p>
            <p>That means choosing the right model for the task, not the most impressive one. It means designing for failure modes, not just happy paths. And it means writing AI into your system in a way that a future engineer can understand and change.</p>
            <p>I work with a small number of clients at a time. If you're building something with AI and want it done right, reach out.</p>
          </div>
          <div class="about-stats reveal reveal-delay-3">
            <div class="stat-box">
              <div class="stat-number">LLM</div>
              <div class="stat-label">Integration, fine-tuning, evaluation</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">RAG</div>
              <div class="stat-label">Vector search, retrieval pipelines</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">Rust</div>
              <div class="stat-label">High-performance AI inference backends</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">Edge</div>
              <div class="stat-label">Serverless AI on Cloudflare Workers</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="contact-section" id="contact">
      <div class="section-inner">
        <p class="section-label reveal">Get In Touch</p>
        <h2 class="section-heading reveal reveal-delay-1">Ready to build with AI?</h2>
        <div class="contact-layout">
          <div class="contact-aside reveal reveal-delay-2">
            <p>I take on a small number of AI engagements at a time. Tell me what you're building and I'll get back to you within one business day.</p>
            <div class="contact-detail">
              <a href="mailto:hello@ridgecodetech.com">hello@ridgecodetech.com</a>
              <a href="https://ridgecodetech.com">ridgecodetech.com</a>
            </div>
          </div>
          <div class="reveal reveal-delay-3">
            <form class="contact-form" novalidate @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">Name</label>
                  <input v-model="formName" type="text" id="name" name="name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="email">Email</label>
                  <input v-model="formEmail" type="email" id="email" name="email" placeholder="you@company.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="service">Service</label>
                <select v-model="formService" id="service" name="service" required>
                  <option value="" disabled>What are you building?</option>
                  <option value="LLM Integration">LLM Integration</option>
                  <option value="Retrieval-Augmented Generation">Retrieval-Augmented Generation (RAG)</option>
                  <option value="AI Workflow Automation">AI Workflow Automation</option>
                  <option value="Custom Model Training">Custom Model Training</option>
                  <option value="RCT Model Early Access">RCT Model — Early Access</option>
                  <option value="Other">Other / Not sure yet</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">Message</label>
                <textarea v-model="formMessage" id="message" name="message" placeholder="Describe your project, the problem you're solving, and any constraints..." required></textarea>
              </div>
              <div v-if="formStatus.msg" :class="['form-status', formStatus.type]">{{ formStatus.msg }}</div>
              <RctButton type="submit" :loading="formLoading" :disabled="formLoading">Send Message</RctButton>
            </form>
          </div>
        </div>
      </div>
    </section>

    <AgentChat scope="ai" :agent-url="AGENT_URL" />

    <RctFooter :links="footerLinks">
    </RctFooter>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { RctNavbar, RctButton, RctFooter, AgentChat } from '@rct/ui';
import { FOOTER_LINKS, WORKER_URL, AGENT_URL, isValidEmail } from '@rct/shared';

const navLinks = [
  { href: '#services', label: 'Services'  },
  { href: '#model',    label: 'RCT Model' },
  { href: '#about',    label: 'Approach'  },
  { href: '#contact',  label: 'Contact'   },
];

const footerLinks = [
  { href: 'https://ridgecodetech.com', label: 'Home' },
  ...FOOTER_LINKS.filter((l) => l.href !== 'https://ai.ridgecodetech.com'),
] as { href: string; label: string }[];

const formName    = ref('');
const formEmail   = ref('');
const formService = ref('');
const formMessage = ref('');
const formLoading = ref(false);
const formStatus  = ref<{ type: 'success' | 'error' | ''; msg: string }>({ type: '', msg: '' });

async function handleSubmit() {
  const name    = formName.value.trim();
  const email   = formEmail.value.trim();
  const service = formService.value;
  const message = formMessage.value.trim();

  if (!name || !email || !service || !message) {
    formStatus.value = { type: 'error', msg: 'Please fill in all fields before sending.' };
    return;
  }
  if (!isValidEmail(email)) {
    formStatus.value = { type: 'error', msg: 'Please enter a valid email address.' };
    return;
  }

  formLoading.value = true;
  formStatus.value  = { type: '', msg: '' };

  try {
    const res = await fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, email, service, message }),
    });

    if (res.ok) {
      formStatus.value = { type: 'success', msg: "Message sent — I'll be in touch within one business day." };
      formName.value = formEmail.value = formService.value = formMessage.value = '';
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      formStatus.value = { type: 'error', msg: data.error ?? 'Something went wrong. Please try emailing directly.' };
    }
  } catch {
    formStatus.value = { type: 'error', msg: 'Network error — please email hello@ridgecodetech.com directly.' };
  } finally {
    formLoading.value = false;
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
});
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy:       #0f264f;
  --navy-deep:  #091a38;
  --navy-mid:   #1a3a6e;
  --periwinkle: #becdea;
  --steel:      #7a96c1;
  --pale:       #e1ecff;
  --off-white:  #f5f8ff;
  --white:      #ffffff;
  --text-body:  #2c3e5c;
  --text-muted: #6b82a0;
}

html { scroll-behavior: smooth; }

body {
  font-family: 'Inter', sans-serif;
  background: var(--off-white);
  color: var(--text-body);
  overflow-x: hidden;
}

/* SCROLL REVEAL */
.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.65s cubic-bezier(.4,0,.2,1), transform 0.65s cubic-bezier(.4,0,.2,1);
}
.reveal.visible { opacity: 1; transform: none; }
.reveal-delay-1 { transition-delay: 0.1s; }
.reveal-delay-2 { transition-delay: 0.2s; }
.reveal-delay-3 { transition-delay: 0.3s; }
.reveal-delay-4 { transition-delay: 0.4s; }

/* HERO */
.hero {
  min-height: 100vh;
  background: var(--navy-deep);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10rem 4rem 0;
  position: relative;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background:
    radial-gradient(ellipse 60% 50% at 70% 30%, rgba(122,150,193,0.1) 0%, transparent 70%),
    radial-gradient(ellipse 50% 60% at 10% 80%, rgba(26,58,110,0.6) 0%, transparent 60%),
    radial-gradient(ellipse 40% 40% at 90% 70%, rgba(15,38,79,0.5) 0%, transparent 50%);
  pointer-events: none;
}
.hero-content { position: relative; max-width: 820px; }
.hero-eyebrow {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--steel);
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.2s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-headline {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 700;
  line-height: 1.08;
  color: var(--white);
  letter-spacing: -0.02em;
  margin-bottom: 1.75rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.4s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-headline em { font-style: normal; color: var(--periwinkle); }
.hero-sub {
  font-size: 1.15rem;
  font-weight: 300;
  color: var(--periwinkle);
  line-height: 1.7;
  max-width: 540px;
  margin-bottom: 3rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.6s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  opacity: 0;
  animation: fadeUp 0.7s 0.75s cubic-bezier(.4,0,.2,1) forwards;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: none; }
}

/* RIDGE DIVIDER */
.ridge-divider {
  position: relative;
  width: 100%;
  height: 120px;
  background: var(--navy-deep);
  margin-top: 6rem;
}
.ridge-divider svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }

/* SECTION BASE */
section { padding: 6rem 4rem; }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--steel);
  margin-bottom: 1rem;
}
.section-heading {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--navy);
  line-height: 1.2;
  margin-bottom: 1.5rem;
}
.section-body {
  font-size: 1rem;
  font-weight: 300;
  color: var(--text-muted);
  line-height: 1.8;
  max-width: 620px;
  margin-bottom: 0;
}

/* SERVICES */
.services { background: var(--off-white); }
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2px;
  margin-top: 3.5rem;
  border: 2px solid var(--periwinkle);
}
.service-card {
  padding: 2.5rem 2rem;
  background: var(--white);
  border-right: 2px solid var(--periwinkle);
  transition: background 0.2s;
}
.service-card:last-child { border-right: none; }
.service-card:hover { background: var(--pale); }
.service-icon { width: 36px; height: 36px; margin-bottom: 1.25rem; opacity: 0.7; }
.service-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--navy);
  margin-bottom: 0.75rem;
}
.service-desc { font-size: 0.875rem; line-height: 1.7; color: var(--text-muted); font-weight: 300; }

/* CUSTOM MODEL */
.model-section { background: var(--navy); padding: 6rem 4rem; }
.model-section .section-label { color: var(--steel); }
.model-section .section-heading { color: var(--pale); }
.model-section .section-body { color: var(--periwinkle); margin-bottom: 3rem; }
.model-badge {
  display: inline-block;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--periwinkle);
  border: 1px solid var(--navy-mid);
  background: rgba(122,150,193,0.1);
  padding: 0.35rem 0.75rem;
  border-radius: 2px;
  margin-bottom: 1.25rem;
}
.model-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  border: 2px solid var(--navy-mid);
  margin-top: 3rem;
}
.model-card {
  background: rgba(255,255,255,0.03);
  border-right: 2px solid var(--navy-mid);
  padding: 2.5rem 2rem;
  transition: background 0.2s;
}
.model-card:last-child { border-right: none; }
.model-card:hover { background: rgba(122,150,193,0.06); }
.model-card-number {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  color: var(--steel);
  opacity: 0.6;
  margin-bottom: 1rem;
}
.model-card-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--pale);
  margin-bottom: 0.75rem;
}
.model-card-desc { font-size: 0.875rem; line-height: 1.7; color: var(--periwinkle); font-weight: 300; }
.model-note {
  margin-top: 2.5rem;
  font-size: 0.9rem;
  color: var(--periwinkle);
  font-weight: 300;
}
.model-note a {
  color: var(--steel);
  text-decoration: none;
  border-bottom: 1px solid var(--navy-mid);
  padding-bottom: 1px;
  transition: color 0.2s, border-color 0.2s;
}
.model-note a:hover { color: var(--pale); border-color: var(--steel); }

/* ABOUT */
.about { background: var(--off-white); }
.about-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  margin-top: 3rem;
}
.about-text p { font-size: 1rem; line-height: 1.85; color: var(--text-muted); font-weight: 300; margin-bottom: 1.25rem; }
.about-text p:last-child { margin-bottom: 0; }
.about-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5px;
  background: var(--periwinkle);
  border: 1.5px solid var(--periwinkle);
}
.stat-box { background: var(--white); padding: 2rem 1.75rem; transition: background 0.2s; }
.stat-box:hover { background: var(--pale); }
.stat-number {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 0.5rem;
}
.stat-label { font-size: 0.8rem; color: var(--text-muted); font-weight: 400; line-height: 1.4; }

/* CONTACT */
.contact-section { background: var(--navy-deep); padding: 7rem 4rem; }
.contact-section .section-label { color: var(--steel); }
.contact-section .section-heading { color: var(--pale); }
.contact-layout {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 5rem;
  align-items: start;
  margin-top: 3rem;
}
.contact-aside p {
  font-size: 0.9rem;
  color: var(--periwinkle);
  line-height: 1.8;
  font-weight: 300;
  margin-bottom: 2rem;
}
.contact-detail { display: flex; flex-direction: column; gap: 0.75rem; }
.contact-detail a {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  color: var(--steel);
  text-decoration: none;
  border-bottom: 1px solid var(--navy-mid);
  padding-bottom: 2px;
  width: fit-content;
  transition: color 0.2s, border-color 0.2s;
}
.contact-detail a:hover { color: var(--pale); border-color: var(--steel); }
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--steel);
}
.form-group input,
.form-group select,
.form-group textarea {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 300;
  color: var(--pale);
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--navy-mid);
  border-radius: 3px;
  padding: 0.8rem 1rem;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  width: 100%;
}
.form-group select option { background: var(--navy-deep); color: var(--pale); }
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus { border-color: var(--steel); background: rgba(255,255,255,0.08); }
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder,
.form-group textarea::placeholder { color: var(--text-muted); }
.form-status {
  font-size: 0.875rem;
  padding: 0.75rem 1rem;
  border-radius: 3px;
}
.form-status.success { background: rgba(122,150,193,0.15); border: 1px solid var(--steel); color: var(--pale); }
.form-status.error   { background: rgba(180,60,60,0.15);  border: 1px solid #b44; color: #f5a0a0; }

/* RESPONSIVE */
@media (max-width: 900px) {
  .contact-layout { grid-template-columns: 1fr; gap: 3rem; }
  .about-layout   { grid-template-columns: 1fr; gap: 3rem; }
  .model-grid     { grid-template-columns: 1fr; }
  .model-card     { border-right: none; border-bottom: 2px solid var(--navy-mid); }
  .model-card:last-child { border-bottom: none; }
}
@media (max-width: 768px) {
  section { padding: 4rem 1.5rem; }
  .hero   { padding: 8rem 1.5rem 0; }
  .services-grid { grid-template-columns: 1fr; }
  .service-card  { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .service-card:last-child { border-bottom: none; }
  .form-row { grid-template-columns: 1fr; }
  .contact-section { padding: 4rem 1.5rem; }
  .model-section   { padding: 4rem 1.5rem; }
}
@media (prefers-reduced-motion: reduce) {
  *, .hero-eyebrow, .hero-headline, .hero-sub, .hero-actions {
    animation: none !important;
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
  .reveal { opacity: 1; transform: none; }
}
</style>
